import React, { useEffect, useState } from "react";
import {
  Rocket,
  Code,
  Zap,
  ArrowLeft
} from "lucide-react";

// Main Engine Components
import MainEngineSlider from "../../components/Vessel/VesselMachinery/MainEngine/MainEngineSlider";
import CurrentVoyageData from "../../components/Vessel/VesselMachinery/MainEngine/CurrentVoyageData";
import MEFuel from "../../components/Vessel/VesselMachinery/MainEngine/MEFuelData";
import EnginePerformanceChart2 from "../../components/Vessel/VesselMachinery/MainEngine/EnginePerformanceChart2";
import VoyagePerformance from "../../components/Vessel/VesselMachinery/MainEngine/VoyagePerformance";
import EnginePropulsionAnalysis from "../../components/Vessel/VesselMachinery/MainEngine/EnginePropulsionAnalysis";
import DateRangePicker from "../../components/Common/DateRangePicker";
import VoyageFilter from "../../components/Common/VoyageFilter";

// Auxiliary Engine Components
import AuxiliaryEngineData from "../../components/Vessel/VesselMachinery/AuxillaryEngine/AuxiliaryEngineData";
import AuxillaryEngineSlider from "../../components/Vessel/VesselMachinery/AuxillaryEngine/AuxillaryEngineSlider";
import AEFuelData from "../../components/Vessel/VesselMachinery/AuxillaryEngine/AEFuelData";
import AEPowerGeneratorChart from "../../components/Vessel/VesselMachinery/AuxillaryEngine/AEPowerGeneratorChart";
import AEFuelConsChart from "../../components/Vessel/VesselMachinery/AuxillaryEngine/AEFuelConsChart";
import AEMcrLoadComparisonChart from "../../components/Vessel/VesselMachinery/AuxillaryEngine/AEMcrLoadComparisonChart";
import VesselHeader from "../../components/Vessel/VesselHeader";
import { getAllVessels } from "../../services/vesselService";
import {
  getVesselMainEngineData,
  getVesselFuelVsMcr,
  getVesselFuelVsRpm,
  getVesselRpmVsMcr,
  getVesselAuxEngineFuelConsData,
  getVesselAuxEngineSfocVsRpmData,
  getVesselAuxEngines,
  getVesselMainEngines,
} from "../../services/vesselMachineryService";

import MachineryBoiler from "../../components/Vessel/VesselMachinery/Boiler/MachineryBoiler";
import MachineryIncinerator from "../../components/Vessel/VesselMachinery/Incinerator/MachineryIncinerator";
import MachineryIGG from "../../components/Vessel/VesselMachinery/IGG/MachineryIGG";

import { useDispatch, useSelector } from "react-redux";
import { setSelectedAE } from "../../store/aeSlice";
import {
  setSelectedVoyage,
  clearSelectedVoyage,
} from "../../store/voyageFilterSlice";

const VesselMachinery = () => {
  const engineColors = [
    { color: "#1f77b4", bgColor: "#e6f0fa" },
    { color: "#ff7f0e", bgColor: "#fff2e6" },
    { color: "#2ca02c", bgColor: "#e6f9e6" },
    { color: "#d62728", bgColor: "#fde6e6" },
    { color: "#9467bd", bgColor: "#f3e6fa" },
    { color: "#8c564b", bgColor: "#f5ebe6" },
    { color: "#e377c2", bgColor: "#fae6f3" },
    { color: "#7f7f7f", bgColor: "#f2f2f2" },
    { color: "#bcbd22", bgColor: "#f9fae6" },
    { color: "#17becf", bgColor: "#e6f7fa" },
  ];

  const assignEngineColors = (engines) =>
    engines.map((engine, index) => ({
      ...engine,
      color: engineColors[index % engineColors.length].color,
      bgColor: engineColors[index % engineColors.length].bgColor,
    }));

  const dispatch = useDispatch();
  const selectedAE = useSelector((state) => state.ae.selectedAE);
  const { currentVessel, setCurrentVessel } = useSelector(
    (state) => state.vesselOverview
  );
  const [activeTab, setActiveTab] = useState("main");
  const [auxiliaryEngines, setAuxiliaryEngines] = useState([]);
  const { startDate, endDate } = useSelector((state) => state.dateRange);
  const [mainEngineData, setMainEngineData] = useState(null);
  const [selectedME, setSelectedME] = useState("me1"); // Default to ME1
  const [selectedIncinerator, setSelectedIncinerator] = useState("inc1");
  const [showDropdown, setShowDropdown] = useState(false);
  const [fuelVsMCR, setFuelVsMCR] = useState([]);
  const [rpmVsMCR, setRpmVsMCR] = useState([]);
  const [fuelVsRPM, setFuelVsRPM] = useState([]);
  const [aeFuelConsData, setAeFuelConsData] = useState(null);
  const [aeSfocVsRpmConsData, setAeFuelSfocVsRpmConsData] = useState(null);
  const [aeLoadData, setAeLoadData] = useState(null);
  const [mainEngineOptions, setMainEngineOptions] = useState([]);
  const [auxiliaryEngineOptions, setAuxiliaryEngineOptions] = useState([]);

  //igg data
  const { selectedVoyage } = useSelector((state) => state.voyageFilter);
  const [iggData, setIggData] = useState({});
  const iggType = currentVessel?.iggType;
  // console.log("iggType:", iggType);
  const [dateRangeDisabled, setDateDisabled] = useState(false);

useEffect(() => {
  const fetchEngines = async () => {
    try {
      const [mainRes, auxRes] = await Promise.all([
        getVesselMainEngines(currentVessel._id),
        getVesselAuxEngines(currentVessel._id),
      ]);

      const mainEngines = assignEngineColors(mainRes.data || mainRes);
      let auxEngines = assignEngineColors(auxRes.data || auxRes);

      const filteredMain = mainEngines
        .map(engine => ({
          ...engine,
          name: engine.name?.toUpperCase() || "",
        }))
        .filter(engine => /^ME[12]$/.test(engine.name));

      const filteredAux = auxEngines
        .map(engine => ({
          ...engine,
          name: engine.name?.toUpperCase() || "",
        }))
        .filter(engine => /^AE[1-6]$/.test(engine.name));

      const mainNames = new Set(filteredMain.map(e => e.name));
      const finalAux = filteredAux.filter(e => !mainNames.has(e.name));

      setMainEngineOptions(filteredMain);
      setAuxiliaryEngineOptions(finalAux);
      setAuxiliaryEngines(finalAux);

      console.log('Filtered Main Engines:', filteredMain.map(e => e.name));
      console.log('Filtered Aux Engines:', finalAux.map(e => e.name));

    } catch (err) {
      console.error("Error fetching engine lists:", err);
    }
  };

  if (currentVessel?._id) fetchEngines();
}, [currentVessel?._id]);

  useEffect(() => {
    dispatch(clearSelectedVoyage());
  }, [currentVessel?._id, dispatch]);

  useEffect(() => {
    setDateDisabled(!!selectedVoyage);
  }, [selectedVoyage]);

  const fetchMainEngineData = async (
    vesselId = currentVessel?._id || currentVessel?.id,
    engineId = selectedME
    // start = startDate,
    // end = endDate
  ) => {
    try {
      const response = await getVesselMainEngineData(
        vesselId,
        engineId,
        selectedVoyage ? null : startDate,
        selectedVoyage ? null : endDate,
        selectedVoyage?._id || null
      );
      setMainEngineData(response.data);
      console.log("ResponeMainEngineVoyage:", response.data);
    } catch (error) {
      console.error("Failed to fetch main engine data:", error);
    }
  };

  const fetchAuxEngineFuelConsData = async (
    vesselId = currentVessel?._id || currentVessel?.id,
    engineId = selectedAE
  ) => {
    try {
      const response = await getVesselAuxEngineFuelConsData(
        vesselId,
        engineId,
        selectedVoyage ? null : startDate,
        selectedVoyage ? null : endDate,
        selectedVoyage?._id || null
      );
      console.log("selectedAE:", selectedAE);
      setAeFuelConsData(response);
      console.log("AE Fuel Consumption:", response);
    } catch (error) {
      console.error("Failed to fetch AE fuel consumption data:", error);
    }
  };

  const fetchAuxEngineSfocVsRpmConsData = async (
    vesselId = currentVessel?._id || currentVessel?.id,
    engineId = selectedAE
  ) => {
    try {
      const response = await getVesselAuxEngineSfocVsRpmData(
        vesselId,
        engineId,
        selectedVoyage ? null : startDate,
        selectedVoyage ? null : endDate,
        selectedVoyage?._id || null
      );
      console.log("selectedAE:", selectedAE);
      setAeFuelSfocVsRpmConsData(response);
      console.log("AE Fuel Consumption:", response);
    } catch (error) {
      console.error("Failed to fetch AE fuel consumption data:", error);
    }
  };

  const handleAuxiliaryEngineSelect = (engineId) => {
    dispatch(setSelectedAE(engineId));
    setShowDropdown(false);
  };

  useEffect(() => {
    const fetchVessels = async () => {
      try {
        const response = await getAllVessels();
        if (response.data?.length) {
          dispatch(setCurrentVessel(response.data[0]));

          fetchMainEngineData(response.data[0]._id);
        }
      } catch (error) {
        console.error("Failed to fetch vessels:", error);
      }
    };

    fetchVessels();
  }, []);

  const fetchEnginePropulsionData = async (
    vesselId = currentVessel?._id || currentVessel?.id,
    engineId = selectedME
  ) => {
            console.log("check1");
    try {
      const [fuelMCRRes, rpmMCRRes, fuelRPMRes] = await Promise.all([
        getVesselFuelVsMcr(
          vesselId,
          engineId,
          selectedVoyage ? null : startDate,
          selectedVoyage ? null : endDate,
          selectedVoyage?._id || null
        ),
        getVesselFuelVsRpm(
          vesselId,
          engineId,
          selectedVoyage ? null : startDate,
          selectedVoyage ? null : endDate,
          selectedVoyage?._id || null
        ),
        getVesselRpmVsMcr(
          vesselId,
          engineId,
          selectedVoyage ? null : startDate,
          selectedVoyage ? null : endDate,
          selectedVoyage?._id || null
        ),
      ]);

      setFuelVsMCR(fuelMCRRes || []);
      setRpmVsMCR(rpmMCRRes || []);
      setFuelVsRPM(fuelRPMRes || []);

      console.log("Fuel vs MCR:", fuelMCRRes);
      console.log("RPM vs MCR:", rpmMCRRes);
      console.log("Fuel vs RPM:", fuelRPMRes);
    } catch (error) {
      console.error("Failed to fetch Engine Propulsion data:", error);
    }
  };

  useEffect(() => {
    if (currentVessel?._id || currentVessel?.id) {
      fetchMainEngineData(
        currentVessel._id || currentVessel.id,
        selectedME,
        startDate,
        endDate
      );
      fetchEnginePropulsionData(
        currentVessel._id || currentVessel.id,
        selectedME,
        startDate,
        endDate
      );
    }
  }, [currentVessel, selectedME, startDate, endDate, selectedVoyage]);

  useEffect(() => {
    if (currentVessel?._id) {
      fetchAuxEngineFuelConsData(
        currentVessel?._id || currentVessel?.id,
        selectedAE
      );
    }
  }, [
    currentVessel,
    selectedAE,
    startDate,
    endDate,
    selectedVoyage,
    activeTab,
  ]);

  useEffect(() => {
    if (currentVessel?._id) {
      fetchAuxEngineSfocVsRpmConsData(
        currentVessel?._id || currentVessel?.id,
        selectedAE
      );
    }
  }, [
    currentVessel,
    selectedAE,
    startDate,
    endDate,
    selectedVoyage,
    activeTab,
  ]);

  // Reset voyage only when vessel changes
  useEffect(() => {
    if (currentVessel?._id) {
      dispatch(setSelectedVoyage(null));
    }
  }, [currentVessel]);

  const handleVesselChange = (selectedVessel) => {
    dispatch(setCurrentVessel(selectedVessel));
    fetchMainEngineData(selectedVessel._id, selectedME, startDate, endDate);
  };

  const handleMESelect = (engineId) => {
    setSelectedME(engineId);
    fetchMainEngineData(currentVessel.id, engineId, startDate, endDate);
  };

  const currentAuxiliaryEngine =
    auxiliaryEngines.find((engine) => engine.id === selectedAE) ||
    auxiliaryEngines[0];

  function normalizeFuelType(fuelType) {
    console.log("change fuel format", fuelType);
    if (!fuelType || typeof fuelType !== "string") return fuelType;
    return fuelType.replace(/-BDN-/, "-F-");
  }

  return (
    <div className="w-full mx-auto space-y-6">
      {/* Header with vessel selector and tabs */}

      <div className="flex justify-between">
        <VesselHeader pageTitle="Machinery Analytics" />

        <div className="flex">
          <VoyageFilter />
          <DateRangePicker disabled={dateRangeDisabled} />
        </div>
      </div>

      <div className="flex flex-col space-y-4 flex-row items-center justify-between">
        {/* Modern segmented control */}
        <div className="inline-flex p-1 bg-gray-50 rounded-xl border border-gray-200 shadow-sm overflow-auto">
          {[
            { label: "Main Engine", value: "main" },
            { label: "Auxiliary Engine", value: "auxiliary" },
            { label: "Boiler", value: "boiler" },
            { label: "Incinerator", value: "incinerator" },
            // { label: "Emergency Fire Pump", value: "firePump" },
            { label: "Inert Gas Generator", value: "igg" },
          ].map((tab) => (
            <button
              key={tab.value}
              className={`px-5 py-2 rounded-lg text-sm font-medium transition-all duration-300 flex items-center relative overflow-hidden ${
                activeTab === tab.value
                  ? "text-white bg-gradient-to-r from-[#27ade3] to-[#2e6bdb] shadow-md"
                  : "text-gray-500 hover:text-gray-700 hover:bg-gray-100"
              }`}
              onClick={() => setActiveTab(tab.value)}
            >
              <span className="relative z-10">{tab.label}</span>
              {activeTab === tab.value && (
                <span className="absolute inset-0 bg-gradient-to-r from-[#27ade3]/80 to-[#2e6bdb]/80 opacity-0 hover:opacity-100 transition-opacity duration-300"></span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Content area */}
      <div className="space-y-6">
        {activeTab === "main" && (
          <>
            <div className="flex items-center justify-center space-x-2 mb-6">
              {mainEngineOptions.map((engine) => (
                <button
                  key={engine.id}
                  onClick={() => setSelectedME(engine.id)}
                  className={`flex items-center px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    engine.id === selectedME
                      ? `text-white ${engine.bgColor.replace(
                          "bg",
                          "bg"
                        )} shadow-md`
                      : `text-gray-700 bg-gray-100 hover:bg-gray-200`
                  }`}
                  style={{
                    backgroundColor:
                      engine.id === selectedME ? engine.color : undefined,
                    border:
                      engine.id === selectedME
                        ? `1px solid ${engine.color}`
                        : "1px solid #e5e7eb",
                  }}
                >
                  <span
                    className="w-2 h-2 rounded-full mr-2"
                    style={{
                      backgroundColor:
                        engine.id === selectedME ? "#ffff" : engine.color,
                    }}
                  ></span>
                  {engine.name}
                </button>
              ))}
            </div>

            <div className="flex gap-6 h-full">
              <div className="overflow-hidden flex">
                <MainEngineSlider
                  selectedEngine={selectedME}
                  className="flex-1"
                />
              </div>
              <div className="space-y-4 flex flex-col flex-1">
                <div className="flex-1">
                  <CurrentVoyageData
                    voyageData={mainEngineData?.voyage || {}}
                    selectedEngine={selectedME}
                    totalFuelConsumptionMT={
                      mainEngineData?.totalFuelConsumptionMT ?? 0
                    }
                  />
                </div>
                <div className="flex-1">
                  <MEFuel
                    latestMeFuelData={mainEngineData?.latestMainEngine || {}}
                    selectedEngine={selectedME}
                    fuelId={
                      normalizeFuelType(mainEngineData?.runningBdnId) || "N/A"
                    }
                    meFuelType={mainEngineData?.allFuelTypes?.meFuelTypes}
                  />
                </div>
              </div>
            </div>

            <div className="mb-6">
              <EnginePerformanceChart2
                mainEngineData={mainEngineData}
                selectedEngine={selectedME}
              />
            </div>

            <div className="mb-6">
              <VoyagePerformance
                selectedEngine={selectedME}
                voyageId={selectedVoyage?._id || null}
              />
            </div>

            <div className="">
              <EnginePropulsionAnalysis
                selectedEngine={selectedME}
                fuelVsMCR={fuelVsMCR}
                rpmVsMCR={rpmVsMCR}
                fuelVsRPM={fuelVsRPM}
              />
            </div>
          </>
        )}
        {activeTab === "auxiliary" && (
          <>
            <div className="flex flex-col gap-6">
              <div className="flex items-center justify-center space-x-2">
                {auxiliaryEngineOptions.map((engine) => (
                  <button
                    key={engine.id}
                    onClick={() => handleAuxiliaryEngineSelect(engine.id)}
                    className={`flex items-center px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                      engine.id === selectedAE
                        ? `text-white ${engine.bgColor.replace(
                            "bg",
                            "bg"
                          )} shadow-md`
                        : `text-gray-700 bg-gray-100 hover:bg-gray-200`
                    }`}
                    style={{
                      backgroundColor:
                        engine.id === selectedAE ? engine.color : undefined,
                      border:
                        engine.id === selectedAE
                          ? `1px solid ${engine.color}`
                          : "1px solid #e5e7eb",
                    }}
                  >
                    <span
                      className="w-2 h-2 rounded-full mr-2"
                      style={{
                        backgroundColor:
                          engine.id === selectedAE ? "#ffff" : engine.color,
                      }}
                    ></span>
                    {engine.name}
                  </button>
                ))}
              </div>
              <div className="overflow-hidden flex gap-6 justify-between">
                <AuxillaryEngineSlider />
                <AuxiliaryEngineData aeLoadData={aeLoadData} />
              </div>
              <div className="flex gap-6">
                <AEFuelData
                  aeFuelConsData={aeFuelConsData}
                  fuelId={
                    normalizeFuelType(mainEngineData?.runningBdnId) || "N/A"
                  }
                />
                <AEPowerGeneratorChart />
                {/* <AECounterData /> */}
              </div>

              <div className="flex-1 flex flex-col gap-6">
                <AEFuelConsChart aeFuelConsData={aeFuelConsData} />
                <AEMcrLoadComparisonChart
                  aeSfocVsRpmConsData={aeSfocVsRpmConsData}
                />
                {/* <BoilerFuelConsChart /> */}
              </div>
            </div>
          </>
        )}
        {activeTab === "boiler" && (
          <div className="flex flex-col gap-6">
            <MachineryBoiler />
          </div>
        )}
        {activeTab === "incinerator" && (
          <>
            <div className="flex flex-col gap-6">
              <MachineryIncinerator selectedIncinerator={selectedIncinerator} />
            </div>
          </>
        )}
        {activeTab === "igg" && (
          <>
            <div className="flex flex-col gap-6">
              {/* <MachineryIGG selectedIncinerator={selectedIncinerator} /> */}
              <MachineryIGG igg={iggData} iggType={iggType} />
            </div>
          </>
        )}
        {["firePump"].includes(activeTab) && (
          <div className="min-h-screen bg-gradient-to-br from-white to-[#d9ebff] relative overflow-hidden">
            {/* Animated background grid */}
            <div className="absolute inset-0 opacity-5">
              <div className="grid grid-cols-12 gap-4 h-full">
                {Array.from({ length: 144 }).map((_, i) => (
                  <div
                    key={i}
                    className="border border-[#27ade3] animate-pulse"
                    style={{ animationDelay: `${i * 0.1}s` }}
                  />
                ))}
              </div>
            </div>

            {/* Floating geometric shapes */}
            <div className="absolute top-1/4 left-1/4 w-20 h-20 border-2 border-[#27ade3]/20 rotate-45 animate-spin slow"></div>
            <div className="absolute top-1/3 right-1/4 w-16 h-16 bg-[#27ade3]/10 rounded-full animate-bounce delay-500"></div>
            <div className="absolute bottom-1/4 left-1/3 w-12 h-12 bg-gradient-to-br from-[#27ade3]/20 to-[#1a325d]/20 rotate-12 animate-pulse delay-1000"></div>

            {/* Main content */}
            <div className="relative z-10 flex flex-col items-center justify-center min-h-screen px-4">
              {/* Header with animated icons */}
              <div className="flex items-center gap-6 mb-8">
                <div className="p-4 bg-gradient-to-br from-[#27ade3]/20 to-[#1a325d]/20 rounded-2xl backdrop-blur-sm border border-white/30 shadow-xl">
                  <Rocket className="w-8 h-8 text-[#27ade3] animate-pulse" />
                </div>
                <div className="p-4 bg-gradient-to-br from-[#1a325d]/20 to-[#27ade3]/20 rounded-2xl backdrop-blur-sm border border-white/30 shadow-xl">
                  <Code className="w-8 h-8 text-[#1a325d] animate-pulse delay-300" />
                </div>
                <div className="p-4 bg-gradient-to-br from-[#27ade3]/20 to-white/20 rounded-2xl backdrop-blur-sm border border-white/30 shadow-xl">
                  <Zap className="w-8 h-8 text-[#27ade3] animate-pulse delay-700" />
                </div>
              </div>

              {/* Main text with staggered animation */}
              <div className="text-center space-y-6 mb-12">
                <div className="space-y-4">
                  <h1 className="text-5xl md:text-6xl font-black text-[#0a2540] leading-tight">
                    Under Development
                  </h1>
                  <div className="h-1 w-32 bg-gradient-to-r from-[#27ade3] to-[#1a325d] mx-auto rounded-full"></div>
                </div>

                <p className="text-lg text-[#0a2540]/60 max-w-lg mx-auto leading-relaxed">
                  This page is currently under construction. Our developers are
                  working around the clock to bring you an amazing experience.
                </p>
              </div>

              {/* Action buttons */}
              <div className="mt-12 flex flex-col sm:flex-row gap-4 items-center">
                <button
                  onClick={() => window.history.back()}
                  className="group px-8 py-4 bg-gradient-to-r from-[#27ade3] to-[#1a325d] text-white rounded-xl font-semibold shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 hover:scale-105 flex items-center gap-3 relative overflow-hidden"
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-white/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  <ArrowLeft className="w-5 h-5 transition-transform group-hover:-translate-x-1 relative z-10" />
                  <span className="relative z-10">Go Back</span>
                </button>
              </div>
            </div>

            {/* Bottom decorative wave */}
            <div className="absolute bottom-0 left-0 w-full">
              <svg
                className="w-full h-32 fill-[#d9ebff]/30"
                viewBox="0 0 1200 120"
                preserveAspectRatio="none"
              >
                <path
                  d="M0,0V46.29c47.79,22.2,103.59,32.17,158,28,70.36-5.37,136.33-33.31,206.8-37.5C438.64,32.43,512.34,53.67,583,72.05c69.27,18,138.3,24.88,209.4,13.08,36.15-6,69.85-17.84,104.45-29.34C989.49,25,1113-14.29,1200,52.47V0Z"
                  opacity=".25"
                ></path>
                <path
                  d="M0,0V15.81C13,36.92,27.64,56.86,47.69,72.05,99.41,111.27,165,111,224.58,91.58c31.15-10.15,60.09-26.07,89.67-39.8,40.92-19,84.73-46,130.83-49.67,36.26-2.85,70.9,9.42,98.6,31.56,31.77,25.39,62.32,62,103.63,73,40.44,10.79,81.35-6.69,119.13-24.28s75.16-39,116.92-43.05c59.73-5.85,113.28,22.88,168.9,38.84,30.2,8.66,59,6.17,87.09-7.5,22.43-10.89,48-26.93,60.65-49.24V0Z"
                  opacity=".5"
                ></path>
                <path d="M0,0V5.63C149.93,59,314.09,71.32,475.83,42.57c43-7.64,84.23-20.12,127.61-26.46,59-8.63,112.48,12.24,165.56,35.4C827.93,77.22,886,95.24,951.2,90c86.53-7,172.46-45.71,248.8-84.81V0Z"></path>
              </svg>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default VesselMachinery;
